import { Dialog as BaseDialog } from '@base-ui/react/dialog'
import { X } from 'lucide-react'
import { type ReactNode } from 'react'

import { useScrollEdges } from '../../../hooks/use-scroll-edges.ts'
import { cn } from '../../../lib/utils.ts'
import { Button } from '../../ui/button/button.tsx'
import { Dialog as DialogPrimitive } from '../../ui/dialog/dialog.tsx'

/**
 * `Dialog` is the controlled dialog shell: it pre-wires `Root` + `Portal` + `Backdrop` + `Popup`
 * and re-exposes the primitive's parts as slots, so a call site writes only its own content.
 *
 * It also owns the parts that have no Base UI counterpart — `Header`, `Body`, `Footer` and the
 * built-in ✕ `CloseIconButton` — since `ui/dialog` mirrors the Base UI namespace 1:1.
 * `Header`, `Body` and `Footer` rely on this shell to coordinate adjacent padding. When
 * composing `ui/dialog` directly, provide your own layout and spacing inside its `Popup`.
 *
 * It is controlled by design — children render inside the popup, so there is no place for a
 * `Trigger`. For an uncontrolled or trigger-driven dialog, compose `ui/dialog` directly.
 *
 * - `Body` is the only scrollable part; `Header` and `Footer` do not shrink. It marks its scrolled
 *   edges with `data-scrolled-top` / `data-scrolled-bottom`, styled as dividers so scrolled-past
 *   content doesn't read as clipped under the Header/Footer.
 * - `Close` is the headless close action from Base UI, for turning your own element into a close
 *   affordance (`render={<Button>Cancel</Button>}`). `CloseIconButton` is the ready-made ✕.
 *
 * @summary Pre-wired controlled dialog (header / body / footer slots).
 */
function DialogShell(props: DialogShell.Props) {
  const { open, onClose, size, blur = false, children } = props
  const handleOpenChange = (nextOpen: boolean): void => {
    if (!nextOpen) onClose()
  }
  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Backdrop blur={blur} />
        <DialogPrimitive.Popup
          size={size}
          className={cn(
            `
              [&>[data-slot='dialog-body']+[data-slot='dialog-footer']]:pt-[calc(var(--spacing-card)-var(--spacing-10))]
              [&>[data-slot='dialog-body']:has(+[data-slot='dialog-footer'])]:pb-10
              [&>[data-slot='dialog-header']+[data-slot='dialog-body']]:pt-10
              [&>[data-slot='dialog-header']:has(+[data-slot='dialog-body'])]:pb-[calc(var(--spacing-card)-var(--spacing-10))]
            `,
          )}
        >
          {children}
        </DialogPrimitive.Popup>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
namespace DialogShell {
  export type Props = {
    open: boolean
    /** Called whenever the dialog asks to close: Escape, backdrop click, or any `Close` part. */
    onClose: () => void
    /** Max width on desktop. Auto-collapses to fullscreen below the `md` breakpoint. @default 'medium' */
    size?: DialogPrimitive.Popup.Props['size']
    /** Apply a 12 px backdrop blur to the page behind the dialog. @default false */
    blur?: boolean
    children: ReactNode
  }
}

function DialogHeader(props: DialogHeader.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex shrink-0 items-center justify-between gap-50 p-card', className)}
      {...rest}
    />
  )
}
namespace DialogHeader {
  export type Props = React.ComponentProps<'div'>
}

function DialogTitle(props: DialogTitle.Props) {
  const { className, ...rest } = props
  return <DialogPrimitive.Title className={cn('flex-1', className)} {...rest} />
}
namespace DialogTitle {
  export type Props = DialogPrimitive.Title.Props
}

/**
 * The ✕ renders as a bordered `secondary` icon button because the design system's dialog header
 * specifies that look for every dialog, so don't revert it to `variant="text"` for a single screen.
 */
function DialogCloseIconButton(props: DialogCloseIconButton.Props) {
  const { className, ...rest } = props
  return (
    <BaseDialog.Close
      data-slot="dialog-close-icon-button"
      render={<Button variant="secondary" size="icon-small" className={className} />}
      {...rest}
    >
      <X />
    </BaseDialog.Close>
  )
}
namespace DialogCloseIconButton {
  export type Props = Omit<BaseDialog.Close.Props, 'children' | 'render'> & {
    /** Required: the button is icon-only, so it has no text to name it. */
    'aria-label': string
  }
}

function DialogBody(props: DialogBody.Props) {
  const { className, ...rest } = props
  const { ref, scrollable, scrolledTop, scrolledBottom } = useScrollEdges<HTMLDivElement>()
  return (
    <div
      data-slot="dialog-body"
      ref={ref}
      // Chrome and Safari never focus a scroll container. Without a tab stop, a Body holding no
      // focusable content cannot be scrolled by keyboard at all. A Body that fits needs no stop.
      tabIndex={scrollable ? 0 : undefined}
      data-scrolled-top={scrolledTop || undefined}
      data-scrolled-bottom={scrolledBottom || undefined}
      className={cn(
        `
          flex min-h-0 flex-1 flex-col gap-70 overflow-y-auto border-line-base-basic px-card py-card
          focus-visible:outline-3 focus-visible:-outline-offset-3 focus-visible:outline-line-base-input-focus/50
          focus-visible:outline-solid
          data-[scrolled-bottom]:border-b
          data-[scrolled-top]:border-t
          max-md:gap-60
        `,
        className,
      )}
      {...rest}
    />
  )
}
namespace DialogBody {
  // `ref` is owned by `useScrollEdges` — the hook needs it to track scroll/resize/mutation, so a
  // consumer-supplied `ref` here would silently replace it and stop the dividers from updating.
  export type Props = Omit<React.ComponentProps<'div'>, 'ref'>
}

function DialogFooter(props: DialogFooter.Props) {
  const { className, ...rest } = props
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        `
          flex shrink-0 items-center justify-end gap-inline p-card
          max-md:flex-col max-md:pb-[calc(var(--spacing-card)+env(safe-area-inset-bottom))]
          max-md:[&>[data-slot='button']]:w-full
        `,
        className,
      )}
      {...rest}
    />
  )
}
namespace DialogFooter {
  export type Props = React.ComponentProps<'div'>
}

const Dialog = Object.assign(DialogShell, {
  Header: DialogHeader,
  Title: DialogTitle,
  CloseIconButton: DialogCloseIconButton,
  Body: DialogBody,
  Description: DialogPrimitive.Description,
  Footer: DialogFooter,
  Close: DialogPrimitive.Close,
})

namespace Dialog {
  export type Props = DialogShell.Props
  export namespace Header {
    export type Props = DialogHeader.Props
  }
  export namespace Title {
    export type Props = DialogTitle.Props
  }
  export namespace CloseIconButton {
    export type Props = DialogCloseIconButton.Props
  }
  export namespace Body {
    export type Props = DialogBody.Props
  }
  export namespace Description {
    export type Props = DialogPrimitive.Description.Props
  }
  export namespace Footer {
    export type Props = DialogFooter.Props
  }
  export namespace Close {
    export type Props = DialogPrimitive.Close.Props
  }
}

export { Dialog }
export { useDialogAction } from './use-dialog-action.ts'
export { useDialogState } from './use-dialog-state.ts'
